import React from 'react';
import {
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { useDebouncedCallback } from 'use-debounce';
import { Typography, Space, InputNumber } from 'antd';
import { Locale } from '../../../context/AppProvider';
import styles from './ClaimFileViewer.module.scss';

const PAGE_OFFSET = 1;
const ZOOM_SCALE = 0.125;
const DEFAULT_SCALE = 0.9;
const ROTATE_INCREMENT = 90;
const MIN_SCALE = -1 * 0.5;
const MAX_SCALE = 1.5;
const CANVAS_PADDING = 1.01;

interface Props {
  fileUrls?: Record<number, string>;
  currPage: number;
  totalPages: number;
  setCurrPage(page: number): void;
}

const determineImageDirection = (rotate: number): number => {
  // Normalize the angle to the range [0, 360]
  const normalizedAngle = ((rotate % 360) + 360) % 360;

  if (normalizedAngle === 90) {
    return 1;
  }

  if (normalizedAngle === 270) {
    return -1;
  }

  // right side up
  return 0;
};

const ClaimFileViewer: React.FC<Props> = ({
  fileUrls,
  currPage,
  totalPages,
  setCurrPage,
}) => {
  const { messages } = React.useContext(Locale);
  const [scale, setScale] = React.useState(DEFAULT_SCALE);
  const [rotate, setRotate] = React.useState(0);
  const [isToolbarVisible, setIsToolbarVisible] = React.useState(true);
  const [isHoveringToolbar, setIsHoveringToolbar] = React.useState(false);
  const inactivityTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const imageRefs = React.useRef<HTMLImageElement[]>([]);

  const changeScale = React.useCallback((offset: number) => {
    setScale((prevScale) => prevScale + offset);
  }, []);

  const onRotate = React.useCallback((isClockwise: boolean) => {
    setRotate(
      (curr) => curr + (isClockwise ? ROTATE_INCREMENT : -1 * ROTATE_INCREMENT)
    );
  }, []);

  const changePage = React.useCallback(
    (newPage: number) => {
      setCurrPage(newPage);

      if (canvasRef.current) {
        canvasRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    },
    [setCurrPage]
  );

  const resetInactivityTimer = React.useCallback(() => {
    setIsToolbarVisible(true);
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
    inactivityTimeout.current = setTimeout(() => {
      if (!isHoveringToolbar) {
        setIsToolbarVisible(false);
      }
    }, 1000);
  }, [isHoveringToolbar]);

  const handleMouseEnter = React.useCallback(() => {
    setIsHoveringToolbar(true);
    setIsToolbarVisible(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setIsHoveringToolbar(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const drawImage = React.useCallback(() => {
    const image = imageRefs.current[currPage];
    if (!image || !canvasRef.current || !imageContainerRef.current) {
      return;
    }

    const context = canvasRef.current.getContext('2d');

    if (!context) {
      return;
    }

    const aspectRatio = image.height / image.width;
    const containerWidth = imageContainerRef.current.offsetWidth;

    // MARK: calculate canvas dimensions
    const direction = determineImageDirection(rotate);
    const imageWidth = scale * containerWidth;
    const imageHeight = scale * aspectRatio * containerWidth;
    canvasRef.current.width =
      CANVAS_PADDING * (direction === 0 ? imageWidth : imageHeight);
    canvasRef.current.height =
      CANVAS_PADDING * (direction === 0 ? imageHeight : imageWidth);

    // MARK: calculate rotation
    context.translate(
      canvasRef.current.width / 2,
      canvasRef.current.height / 2
    );
    context.rotate((rotate * Math.PI) / 180);
    context.translate(
      -canvasRef.current.width / 2,
      -canvasRef.current.height / 2
    );

    // MARK: draw the image
    context.drawImage(
      image,
      canvasRef.current.width / 2 - imageWidth / 2,
      canvasRef.current.height / 2 - imageHeight / 2,
      imageWidth,
      imageHeight
    );
  }, [rotate, scale, currPage]);

  const onChangePage = React.useCallback(
    (value: number | null) => {
      if (value) {
        if (value < 1) {
          value = 1;
        } else if (value > totalPages) {
          value = totalPages;
        }
        changePage(value);
      }
    },
    [changePage, totalPages]
  );

  const onSubmitChangePage = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      e.preventDefault();

      const value = Number((e.target as HTMLInputElement).value);
      onChangePage(Number.isNaN(value) ? 1 : value);
    },
    [onChangePage]
  );

  const onInputChangePage = useDebouncedCallback((value: number | null) => {
    onChangePage(value);
  }, 1_000);

  // eslint-disable-next-line react-etc/prefer-usememo
  React.useEffect(() => {
    setRotate(0);
  }, [currPage]);

  React.useEffect(() => {
    drawImage();
  }, [drawImage, scale]);

  React.useEffect(() => {
    document.addEventListener('mousemove', resetInactivityTimer);
    resetInactivityTimer();
    return () => {
      document.removeEventListener('mousemove', resetInactivityTimer);
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
    };
  }, [resetInactivityTimer]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onChangePage(currPage - PAGE_OFFSET);
          setIsToolbarVisible(true);
          break;
        case 'ArrowRight':
          event.preventDefault();
          onChangePage(currPage + PAGE_OFFSET);
          setIsToolbarVisible(true);
          break;
      }
    },
    [currPage, onChangePage]
  );

  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      drawImage();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [drawImage]);

  if (!fileUrls) {
    return null;
  }

  return (
    <div className={styles.fileViewerContainer}>
      <div className={styles.imageContainer}>
        <div
          className={styles.imageOverflowContainer}
          ref={imageContainerRef}
          tabIndex={0}
          role="group"
          aria-label={messages['claim_overview.files.viewer.document']}
          onKeyDown={handleKeyDown}
        >
          <canvas
            id="image_canvas"
            data-testid="file-viewer-canvas"
            className={styles.imageCanvas}
            ref={canvasRef}
          />
          {Object.entries(fileUrls).map(([page, url]) => {
            const pageNum = parseInt(page, 10);
            return (
              <img
                key={pageNum}
                ref={(el) => {
                  if (el) {
                    imageRefs.current[pageNum] = el;
                  }
                }}
                alt={`Page ${pageNum}`}
                src={url}
                style={{ display: 'none' }}
                onLoad={pageNum === currPage ? drawImage : undefined}
              />
            );
          })}
        </div>
      </div>
      <div
        data-testid="file-viewer-toolbar"
        className={clsx(styles.toolBar, {
          [styles.hiddenToolBar]: !isToolbarVisible,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Space>
          <Space>
            <ZoomOutOutlined
              data-testid="file-viewer-zoom-out"
              onClick={() => {
                changeScale(-ZOOM_SCALE);
              }}
              className={clsx([
                scale <= MIN_SCALE
                  ? styles.inactiveTooltip
                  : styles.activeTooltip,
              ])}
            />
            <ZoomInOutlined
              data-testid="file-viewer-zoom-in"
              onClick={() => {
                changeScale(ZOOM_SCALE);
              }}
              className={clsx([
                scale >= MAX_SCALE
                  ? styles.inactiveTooltip
                  : styles.activeTooltip,
              ])}
            />
          </Space>
          <Space>
            <LeftOutlined
              onClick={() => {
                changePage(currPage - PAGE_OFFSET);
              }}
              className={clsx([
                currPage <= 1 ? styles.inactiveTooltip : styles.activeTooltip,
              ])}
            />
            <Typography.Text style={{ color: 'white' }}>
              <InputNumber
                aria-label={messages['claim_overview.files.viewer.page_number']}
                className={styles.pageInput}
                min={1}
                max={totalPages}
                defaultValue={1}
                value={currPage}
                onChange={onInputChangePage}
                onPressEnter={onSubmitChangePage}
                controls={false}
                changeOnWheel
                size="small"
              />{' '}
              of {totalPages}
            </Typography.Text>
            <RightOutlined
              onClick={() => {
                changePage(currPage + PAGE_OFFSET);
              }}
              className={clsx([
                currPage >= totalPages
                  ? styles.inactiveTooltip
                  : styles.activeTooltip,
              ])}
            />
          </Space>
          <Space>
            <RotateLeftOutlined
              data-testid="file-viewer-rotate-left"
              onClick={() => {
                onRotate(false);
              }}
              className={styles.activeTooltip}
            />
            <RotateRightOutlined
              data-testid="file-viewer-rotate-right"
              onClick={() => {
                onRotate(true);
              }}
              className={styles.activeTooltip}
            />
          </Space>
        </Space>
      </div>
    </div>
  );
};

export default ClaimFileViewer;
